import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import InstantiateEnterpriseRiskModal from 'src/components/instantiate-enterprise-risk-modal/InstantiateEnterpriseRiskModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { useGetEnterpriseRiskById } from 'src/hooks/queries/enterprise-risk/useGetEnterpriseRiskById';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import { useGetDetailParentPath } from 'src/routes/useGetDetailParentPath';

import { useDeleteEnterpriseRisk } from '@/hooks/mutations/enterprise-risk';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';
import { enterpriseRiskDetailsUrl } from '@/utils/urls';

type Props = {
  selectedTabId: 'details' | 'risks';
  showDeleteButton?: boolean;
};

const Page: FC<Props> = ({ selectedTabId, showDeleteButton }) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showInstantiateModal, setShowInstantiateModal] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const navigate = useNavigate();
  const enterpriseRiskId = useGetGuidParam('id');
  const parentUrl = useGetDetailParentPath(enterpriseRiskId);
  const { deleteEnterpriseRisk, loading: deleting } = useDeleteEnterpriseRisk();
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'enterpriseRisks',
  });
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when dynamic_forms are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const { data, loading } = useGetEnterpriseRiskById({
    queryArgs: { id: enterpriseRiskId },
  });

  const enterpriseRisk = data?.enterprise_risk[0];

  const tabs = useTabs({
    parentType: Parent_Type_Enum.EnterpriseRisk,
    parent: null,
    hrefRoot: enterpriseRiskDetailsUrl(enterpriseRiskId),
  });

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      if (!enterpriseRisk) {
        return false;
      }
      const result = await deleteEnterpriseRisk(enterpriseRisk.Id);
      if (result?.deleteChildEnterpriseRisk?.affected_rows === 1) {
        setIsDeleted(true);
      }
      await navigate(parentUrl);

      return true;
    },
  });

  const counter =
    enterpriseRisk &&
    `(${getFriendlyId(Parent_Type_Enum.EnterpriseRisk, enterpriseRisk.SequentialId)})`;

  const fallbackTitle = st('fallback_title');
  // Check if the enterprise risk has child enterprise risks, which would prevent deletion
  const hasChildren = !!enterpriseRisk?.children?.length;

  // Object-level toolbar (CUS-167) — flag-gated. Relocates Instantiate + Delete
  // into the toolbar's overflow ⋮ menu and surfaces the Save disc / Cancel X
  // (save state bridged from the active tab's form via useObjectToolbarFormStore).
  // Permissions are TYPE-LEVEL (no `parentObject`) to mirror the legacy
  // <Permission> gates on this page — they pass no parentObject, so enterprise
  // risk update/delete resolve against the type, not the instance. Delete tracks
  // the same `!loading && !hasChildren && showDeleteButton` rule as the legacy
  // button via conditional inclusion of the danger action.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: enterpriseRisk?.Title ?? fallbackTitle,
        // Pass the already-parenthesised counter (the toolbar renders it verbatim).
        counter: counter || undefined,
      },
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
          {
            id: 'instantiate',
            actions: [
              {
                id: 'instantiate',
                label: st('instantiateButton'),
                iconName: 'file-plus-02',
                permission: 'update:enterprise_risk',
                onClick: () => setShowInstantiateModal(true),
              },
            ],
          },
        ],
        danger:
          showDeleteButton && !hasChildren && !loading
            ? [
                {
                  id: 'delete',
                  label: st('delete_button'),
                  iconName: 'trash-01',
                  permission: 'delete:enterprise_risk',
                  onClick: () => setIsDeleteModalVisible(true),
                },
              ]
            : undefined,
      },
    }),
    [
      enterpriseRisk?.Title,
      fallbackTitle,
      counter,
      t,
      st,
      formConfig,
      showDeleteButton,
      hasChildren,
      loading,
    ]
  );

  if (isDeleted) {
    return null;
  }

  if (data?.enterprise_risk.length === 0) {
    throw new PageNotFound(
      `Enterprise risk with id ${enterpriseRiskId} not found`
    );
  }

  return (
    <PageLayout
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          {!loading && !hasChildren && showDeleteButton && (
            <Permission permission={'delete:enterprise_risk'}>
              <Button
                variant={'normal'}
                formAction={'none'}
                // Wait for the entity to hydrate. `onDelete` guards on
                // `if (!enterpriseRisk) return false`, which surfaces as a bare
                // "Failed to save changes" with NO request issued, so a click
                // inside the hydration window looks like a broken delete
                // (ENG-616). Disabling also restores Playwright's actionability
                // wait.
                disabled={!enterpriseRisk}
                onClick={() => {
                  setIsDeleteModalVisible(true);
                }}
              >
                {st('delete_button')}
              </Button>
            </Permission>
          )}
          <Permission permission={'update:enterprise_risk'}>
            <Button
              variant={'normal'}
              formAction={'none'}
              onClick={() => {
                setShowInstantiateModal(true);
              }}
            >
              {st('instantiateButton')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
      meta={{
        title: fallbackTitle,
      }}
      title={enterpriseRisk?.Title}
      counter={counter}
    >
      <ControlledTabs
        tabs={tabs}
        activeTabId={selectedTabId}
        variant={'container'}
        parentType={Parent_Type_Enum.EnterpriseRisk}
        disableSettings={objectToolbarEnabled}
      />

      <TabSettingsModal
        parentType={Parent_Type_Enum.EnterpriseRisk}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
      />

      <DeleteModal
        loading={deleting}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>

      <InstantiateEnterpriseRiskModal
        isVisible={showInstantiateModal}
        onDismiss={() => {
          setShowInstantiateModal(false);
        }}
        enterpriseRiskIds={[enterpriseRiskId]}
      />
    </PageLayout>
  );
};

export default Page;
